export type ProvisionStatus =
  "pending" | "in_progress" | "completed" | "failed" | "cancelled";

export type ProvisionStep =
  | "cognito_user"
  | "cognito_group"
  | "user_profile"
  | "business_profile"
  | "stripe_account"
  | "stripe_link"
  | "welcome_email"
  | "done";

/**
 * Mirrors the backend `ProvisioningRequest.provisioningMode` enum. Drives
 * which welcome-email template the cognito-user step issued — useful in
 * detail views to explain why a particular merchant did or didn't get a
 * password-reset link.
 */
export type ProvisioningMode =
  "new_user" | "existing_native" | "existing_sso_migrated";

export interface StepHistoryEntry {
  step: ProvisionStep;
  status: "completed" | "failed";
  timestamp: string;
  error?: string;
}

export interface ProvisioningRequest {
  id: string;
  status: ProvisionStatus;
  currentStep: ProvisionStep;
  stepHistory: StepHistoryEntry[];
  failedStep: string | null;
  failedError: string | null;
  retryCount: number;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  businessName: string;
  businessCountry: "CA" | "US";
  businessType: "individual" | "company" | "non_profit";
  businessCategoryCode: string;
  businessAddress: AddressData;
  individualAddress: AddressData;
  ssnLast4?: string;
  taxId?: string;
  businessUrl?: string;
  supportPhone?: string;
  cognitoUserId?: string;
  userProfileId?: string;
  businessProfileId?: string;
  stripeConnectedAccountId?: string;
  stripeTerminalLocationId?: string;
  provisioningMode?: ProvisioningMode;
  createdBy: string;
  createdByEmail: string;
  source: string;
  launchpadApplicationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AddressData {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface ProvisionInput {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  businessName: string;
  businessCountry: "CA" | "US";
  businessType: "individual" | "company" | "non_profit";
  businessCategoryCode: string;
  businessAddress: AddressData;
  individualAddress: AddressData;
  ssnLast4?: string;
  taxId?: string;
  businessUrl?: string;
  supportPhone?: string;
}

export const STEP_LABELS: Record<ProvisionStep, string> = {
  cognito_user: "Cognito User",
  cognito_group: "Terminal Group",
  user_profile: "User Profile",
  business_profile: "Business Profile",
  stripe_account: "Stripe Connect Account",
  stripe_link: "Link Stripe Account",
  welcome_email: "Welcome Email",
  done: "Done",
};

export type WizardStep = "contact" | "business" | "address" | "review";

// Amplify Gen 2's default selection set on `client.models.X.list/get` does not
// auto-expand referenced custom types (`a.ref("ProvisionAddress")` on the
// backend), which makes AppSync reject the generated query with
// `SubSelectionRequired` for `businessAddress`/`individualAddress`. Declaring
// the fields explicitly — including the nested address subfields — produces a
// query that validates and returns typed objects at runtime.
export const PROVISIONING_SELECTION_SET = [
  "id",
  "status",
  "currentStep",
  "stepHistory",
  "failedStep",
  "failedError",
  "retryCount",
  "email",
  "phone",
  "firstName",
  "lastName",
  "birthDate",
  "businessName",
  "businessCountry",
  "businessType",
  "businessCategoryCode",
  "businessAddress.line1",
  "businessAddress.line2",
  "businessAddress.city",
  "businessAddress.state",
  "businessAddress.postalCode",
  "businessAddress.country",
  "individualAddress.line1",
  "individualAddress.line2",
  "individualAddress.city",
  "individualAddress.state",
  "individualAddress.postalCode",
  "individualAddress.country",
  "ssnLast4",
  "taxId",
  "businessUrl",
  "supportPhone",
  "cognitoUserId",
  "userProfileId",
  "businessProfileId",
  "stripeConnectedAccountId",
  "stripeTerminalLocationId",
  "provisioningMode",
  "createdBy",
  "createdByEmail",
  "source",
  "launchpadApplicationId",
  "created",
  "createdAt",
  "updatedAt",
] as const;
